import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import {
  DEFAULT_TAG_COLOR,
  TAG_COLOR_OPTIONS,
  getTagColorClassName,
  resolveTagColor,
  type TagColor,
} from '@/components/shared/tag-colors';
import type { BackofficeTag } from '../types';

interface TagEditorModalProps {
  tag?: BackofficeTag | null;
  submitting: boolean;
  statusUpdating: boolean;
  protectedTag: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    description?: string;
    color: TagColor;
  }) => Promise<void> | void;
  onToggleStatus: () => Promise<void> | void;
}

export function TagEditorModal({
  tag,
  submitting,
  statusUpdating,
  protectedTag,
  error,
  onClose,
  onSubmit,
  onToggleStatus,
}: TagEditorModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [name, setName] = useState(tag?.name ?? '');
  const [description, setDescription] = useState(tag?.description ?? '');
  const [color, setColor] = useState<TagColor>(resolveTagColor(tag?.color ?? DEFAULT_TAG_COLOR));
  const editing = Boolean(tag);
  const busy = submitting || statusUpdating;

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onClose]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    const trimmedName = name.trim();
    if (!trimmedName) return;

    const trimmedDescription = description.trim();
    await onSubmit(
      trimmedDescription
        ? { name: trimmedName, description: trimmedDescription, color }
        : { name: trimmedName, color }
    );
  }

  const modalTitle = editing ? 'Edit tag' : 'Create tag';
  const submitLabel = editing ? 'Save tag' : 'Create tag';
  const submittingLabel = editing ? 'Saving tag' : 'Creating tag';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-editor-modal-title"
        className="max-h-[92vh] w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="tag-editor-modal-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              {modalTitle}
            </h2>
            <p className="mt-1 text-body text-neutral-500">
              {editing
                ? 'Update the display label, color, description, or archive state for this tag.'
                : 'Tag IDs are generated from the display name and are shared across backoffice entities.'}
            </p>
            {tag ? (
              <p className="mt-1 font-mono text-small text-neutral-500">ID: {tag.id}</p>
            ) : null}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <form
          className="max-h-[calc(92vh-96px)] overflow-y-auto px-6 py-5"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small font-semibold text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid gap-4">
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Name
              <input
                name="name"
                required
                value={name}
                placeholder="Internal tester"
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body font-normal text-neutral-900 placeholder:text-neutral-400"
                onChange={(event) => setName(event.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Description
              <input
                name="description"
                value={description ?? ''}
                placeholder="Optional context for backoffice users"
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body font-normal text-neutral-900 placeholder:text-neutral-400"
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-small font-semibold text-neutral-700">Color</legend>
              <div className="flex flex-wrap gap-2">
                {TAG_COLOR_OPTIONS.map((option) => (
                  <label key={option.id} className="cursor-pointer">
                    <input
                      type="radio"
                      name="color"
                      value={option.id}
                      checked={color === option.id}
                      className="sr-only"
                      onChange={() => setColor(option.id)}
                    />
                    <span
                      className={[
                        'inline-flex rounded-full border px-3 py-1.5 text-small font-semibold transition',
                        getTagColorClassName(option.id),
                        color === option.id ? 'ring-2 ring-neutral-900/20' : 'opacity-70',
                      ].join(' ')}
                    >
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-5">
            {editing ? (
              protectedTag ? (
                <span className="text-small font-medium text-neutral-500">Protected tag</span>
              ) : (
                <button
                  type="button"
                  className="focus-ring inline-flex h-10 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 text-body font-semibold text-neutral-700 transition hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-400"
                  disabled={busy}
                  onClick={() => {
                    void onToggleStatus();
                  }}
                >
                  {statusUpdating ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {statusUpdating
                    ? 'Updating'
                    : tag?.status === 'active'
                      ? 'Archive tag'
                      : 'Restore tag'}
                </button>
              )
            ) : (
              <span />
            )}
            <button
              type="submit"
              className="focus-ring inline-flex h-10 items-center gap-2 rounded-lg border border-backoffice-500 bg-backoffice-500 px-4 text-body font-semibold text-white transition hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-300"
              disabled={!name.trim() || busy}
            >
              {submitting ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
              {submitting ? submittingLabel : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
